import type { OpenTab } from '@/components/features/document-tabs'

export type DocumentDeletedDetail = {
  documentId?: string
  path?: string
  filePath?: string
}

export type FolderDeletedDetail = {
  path: string
  documentsDeleted?: number
}

function normalizeContentPath(p: string): string {
  return String(p || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')
}

/** True when `candidate` is the folder or a path inside it. */
export function pathIsUnderOrEqualFolder(candidate: string, folderPath: string): boolean {
  const c = normalizeContentPath(candidate)
  const f = normalizeContentPath(folderPath)
  if (!c || !f) return false
  return c === f || c.startsWith(`${f}/`)
}

export function parentContentFolderPath(folderPath: string): string | null {
  const norm = normalizeContentPath(folderPath)
  if (!norm) return null
  const idx = norm.lastIndexOf('/')
  if (idx <= 0) return idx === 0 ? '' : null
  return norm.slice(0, idx)
}

/** Tab should close after its folder (or a parent folder) was deleted. */
export function tabAffectedByFolderDelete(tab: OpenTab, deletedFolderPath: string): boolean {
  if (tab.type === 'explorer') {
    const fp = tab.explorerData?.folderPath
    if (fp && pathIsUnderOrEqualFolder(fp, deletedFolderPath)) return true
  }
  const contentPath =
    tab.filePath ?? (tab.id.includes('/') && !tab.id.startsWith('basecamp-') ? tab.id : null)
  if (contentPath && pathIsUnderOrEqualFolder(contentPath, deletedFolderPath)) return true
  return false
}

/** Match an open tab to a document-deleted event (UUID id vs content path). */
export function tabMatchesDeletedDocument(
  tab: OpenTab,
  detail: DocumentDeletedDetail
): boolean {
  const keys = [detail.documentId, detail.path, detail.filePath].filter(
    (k): k is string => typeof k === 'string' && k.length > 0
  )
  if (keys.length === 0) return false

  return keys.some(
    (key) =>
      tab.id === key ||
      tab.filePath === key ||
      (tab.filePath != null && tab.filePath.length > 0 && key.includes('/') && tab.filePath === key)
  )
}

const recentlyDeletedKeys = new Set<string>()

/** Track keys for a short window so loaders skip noisy 404 toasts/retries after intentional delete. */
export function markDocumentRecentlyDeleted(...keys: Array<string | null | undefined>): void {
  for (const key of keys) {
    if (!key) continue
    recentlyDeletedKeys.add(key)
    window.setTimeout(() => recentlyDeletedKeys.delete(key), 60_000)
  }
}

export function wasDocumentRecentlyDeleted(
  documentId: string,
  filePath?: string | null
): boolean {
  if (recentlyDeletedKeys.has(documentId)) return true
  if (filePath && recentlyDeletedKeys.has(filePath)) return true
  return false
}
